import { Box, Typography } from '@mui/material';
import { lapseRibbonSrc, xpCoinSrc } from '../../assets/wallet';

const availableLabelSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: 14,
  lineHeight: '20px',
  letterSpacing: '0.5px',
};

const balanceValueSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 800,
  fontSize: { xs: 28, sm: 36 },
  lineHeight: { xs: '36px', sm: '44px' },
  letterSpacing: '2.5px',
  color: '#fff',
};

/** Figma 9679:1841 — Redeem button */
function RedeemButton({ label, onClick }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 38,
        minWidth: { xs: 140, sm: 184 },
        px: 3,
        borderRadius: '100px',
        border: '1px solid #ffc5f7',
        background: 'linear-gradient(270deg, #ff00f2 0%, #822aff 100%)',
        boxShadow:
          'inset 2px 2px 5px rgba(255, 255, 255, 0.35), inset -2px -2px 6px rgba(0, 0, 0, 0.25)',
        filter: 'drop-shadow(0 4px 0 rgba(0, 0, 0, 0.64))',
        cursor: 'pointer',
        flexShrink: 0,
        '&:hover': { opacity: 0.94 },
      }}
    >
      <Typography
        component="span"
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: 500,
          fontSize: 14,
          lineHeight: '22px',
          color: '#fff',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

/** Figma 9679:1838 — lapse warning ribbon */
function LapseRibbon({ message }) {
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: 675,
        mx: 'auto',
        minHeight: 28,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        component="img"
        src={lapseRibbonSrc}
        alt=""
        aria-hidden="true"
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          display: 'block',
        }}
      />
      <Typography
        sx={{
          position: 'relative',
          zIndex: 1,
          fontFamily: 'Inter, sans-serif',
          fontWeight: 400,
          fontSize: 12,
          lineHeight: '20px',
          letterSpacing: '0.8px',
          color: '#fff',
          textAlign: 'center',
          px: 2,
        }}
      >
        {message}
      </Typography>
    </Box>
  );
}

/** Figma 9679:1830 (gold / XPs) and 10732:13767 (orange / RapCoins) card palettes */
const CARD_VARIANTS = {
  gold: {
    border: '#faed60',
    background: 'linear-gradient(180deg, #d8981e 0%, #fcf062 100%)',
    dashedBorder: '#d8981e',
  },
  orange: {
    border: '#ffd900',
    background: 'linear-gradient(180deg, #d74304 0%, #ffd900 100%)',
    dashedBorder: '#d74304',
  },
  /** Figma 11015:13782/13783 — RapGems palette */
  pink: {
    border: '#f61087',
    background: 'linear-gradient(180deg, #430096 0%, #f61087 100%)',
    dashedBorder: '#430096',
  },
  /** Figma 11059:926/927 — VIP Room palette */
  dark: {
    border: '#929292',
    background: 'linear-gradient(180deg, #000 0%, #929292 100%)',
    dashedBorder: '#000',
    labelColor: 'rgba(255, 255, 255, 0.5)',
  },
};

/** Figma 9679:1828–1831 / 10732:13764 — available balance card */
export default function WalletBalanceCard({
  icon = xpCoinSrc,
  label = 'Available XPs',
  balanceLabel = '0',
  redeemLabel,
  onRedeem,
  warningMessage,
  variant = 'gold',
  children,
}) {
  const palette = CARD_VARIANTS[variant] ?? CARD_VARIANTS.gold;
  const labelColor = palette.labelColor ?? 'rgba(0, 0, 0, 0.5)';

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 745,
        mx: 'auto',
        borderRadius: '10px',
        border: `5px solid ${palette.border}`,
        background: palette.background,
        p: '10px',
      }}
    >
      <Box
        sx={{
          borderRadius: '10px',
          border: `2px dashed ${palette.dashedBorder}`,
          minHeight: 150,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          gap: 1.5,
          px: { xs: 1.5, sm: '25px' },
          pt: (warningMessage || children) ? '18px' : '30px',
          pb: 1.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
            <Box
              component="img"
              src={icon}
              alt=""
              sx={{
                width: { xs: 48, sm: 60 },
                height: { xs: 52, sm: 64 },
                objectFit: 'contain',
                display: 'block',
                flexShrink: 0,
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ ...availableLabelSx, color: labelColor }}>{label}</Typography>
              <Typography sx={balanceValueSx}>{balanceLabel}</Typography>
            </Box>
          </Box>

          {redeemLabel ? <RedeemButton label={redeemLabel} onClick={onRedeem} /> : null}
        </Box>

        {children}

        {warningMessage ? <LapseRibbon message={warningMessage} /> : null}
      </Box>
    </Box>
  );
}
